import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';

import { PaginatedData } from '@shared/models/paginated-data';
import { timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ProfileDal } from '../dal/profile.dal';
import { allAvailableNurses } from '../interfaces/profile.interfaces';
import { Calendar, ProfileCalendarsResponseDto } from '../models/calendar.model';
import { Period } from '../models/period.model';
import { Profile, SearchProfileInput } from '../models/profile.model';

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  constructor(private profileDal: ProfileDal) {}

  getSurgeonsByDayAvailability(
    day: string,
    opening: boolean,
    page: number,
    pageSize: number,
    searchValue: string,
    population: string = ''
  ): Observable<Profile[]> {
    return this.profileDal.getSurgeonsByDayAvailability(day, opening, page, pageSize, searchValue, population);
  }

  getNurseReportPDF(
    profileId: string,
    hospitalId: string,
    periodStart: string,
    periodEnd: string,
    showAnnex: boolean,
    showIndividualExperienceCharts: boolean,
    showRank: boolean,
    data: any
  ): Observable<any> {
    return this.profileDal.getNurseReportPDF(
      profileId,
      hospitalId,
      periodStart,
      periodEnd,
      showAnnex,
      showIndividualExperienceCharts,
      showRank,
      data
    );
  }

  getProfilesWithTimes(
    position?: string,
    day?: string,
    period?: Period,
    type?: any,
    smartplanning?: boolean
  ): Observable<Calendar[]> {
    return this.profileDal.getProfilesWithTimes(position, day, period, type, smartplanning);
  }

  getProfilesByPosition(
    position: string,
    page: number | null,
    limit: number | null,
    name: string,
    justTitular?: boolean,
    allProfiles?: boolean,
    noUrgences?: boolean,
    seniorities?: string,
    isNight?: boolean,
    keepDefaultSpecialtySurgeons?: boolean,
    profileOnTop?: boolean,
    delayTime: number = 0,
    population?: string,
    profilesIds?: string,
    residency?: string,
    options?: { noHospitalsLookup: boolean },
    specialties?: string,
    services?: string,
    includesTimeAlert: boolean = false
  ): Observable<any> {
    if (!position) {
      return this.getProfiles(page, limit, name);
    }

    return timer(delayTime).pipe(
      switchMap(() =>
        this.profileDal.getProfilesByPosition(
          position,
          page,
          limit,
          name,
          justTitular,
          allProfiles,
          noUrgences,
          seniorities,
          isNight,
          keepDefaultSpecialtySurgeons,
          profileOnTop,
          population,
          profilesIds,
          residency,
          options,
          specialties,
          services,
          includesTimeAlert
        )
      )
    );
  }

  searchProfiles(searchProfileInput: SearchProfileInput): Observable<PaginatedData<Profile>> {
    return this.profileDal.searchProfiles(searchProfileInput);
  }

  getProfilesByPositionFromHospital(
    hospital: string | undefined,
    position: string | undefined,
    page: number | undefined,
    limit: number | undefined,
    name: string,
    residency?: string,
    allProfiles?: boolean,
    noUrgences?: boolean,
    seniorities?: string,
    population?: string,
    isNight?: boolean,
    isNoUserAssociate?: boolean,
    specialties?: string
  ): Observable<any> {
    return this.profileDal.getProfilesByPositionFromHospital(
      hospital,
      position,
      page,
      limit,
      name,
      residency,
      allProfiles,
      noUrgences,
      seniorities,
      population,
      isNight,
      isNoUserAssociate,
      specialties
    );
  }

  getProfilesByPositionResidency(
    position: string,
    page: number,
    limit: number,
    name: string,
    residency?: string,
    allNurses?: boolean,
    noUrgences?: boolean
  ): Observable<any> {
    return this.profileDal.getProfilesByPositionResidency(
      position,
      page,
      limit,
      name,
      residency,
      allNurses,
      noUrgences
    );
  }

  getProfilesByPositionResidencyFromHospital(
    hospital: string,
    position: string,
    page: number,
    limit: number,
    name: string,
    residency?: string,
    allNurses?: boolean,
    noUrgences?: boolean
  ): Observable<any> {
    return this.profileDal.getProfilesByPositionResidencyFromHospital(
      hospital,
      position,
      page,
      limit,
      name,
      residency,
      allNurses,
      noUrgences
    );
  }

  getProfilesWithUserByPositionResidencyFromHospital(
    hospital: string,
    position: string,
    page: number | null,
    limit: number | null,
    name: string,
    residency?: string,
    allNurses?: boolean,
    noUrgences?: boolean
  ): Observable<any> {
    return this.profileDal.getProfilesWithUserByPositionResidencyFromHospital(
      hospital,
      position,
      page,
      limit,
      name,
      residency,
      allNurses,
      noUrgences
    );
  }

  //not used
  getProfilesWithAvailabilityByPositionResidencyFromHospital(
    date: string,
    hospital: string,
    position: string,
    page: number,
    limit: number,
    name: string,
    residency?: string,
    allNurses?: boolean,
    noUrgences?: boolean,
    isNight?: boolean
  ): Observable<any> {
    return this.profileDal.getProfilesWithAvailabilityByPositionResidencyFromHospital(
      date,
      hospital,
      position,
      page,
      limit,
      name,
      residency,
      allNurses,
      noUrgences,
      isNight
    );
  }

  getProfilesBySpecialty(position: string) {
    return this.profileDal.getProfilesBySpecialty(position);
  }

  getProfilesBySpecialtyFromHospital(position: string, hospital: string) {
    return this.profileDal.getProfilesBySpecialtyFromHospital(position, hospital);
  }

  getProfilesBySpecialtyInDateFromHospital(position: string, hospital: string, date: string) {
    return this.profileDal.getProfilesBySpecialtyInDateFromHospital(position, hospital, date);
  }

  getProfiles(
    page: number | null,
    limit: number | null,
    name: string,
    noUrgences?: boolean,
    searchType?: any,
    data?: any,
    args?: { noHospitalsLookup: boolean }
  ): Observable<any> {
    return this.profileDal.getProfiles(page, limit, name, noUrgences, searchType, data, args);
  }

  getAvailableAndOutOfRoomProfiles(
    hospitalId: string,
    date: Date | string,
    positions: string[],
    residency: string[],
    seniority: string[]
  ): Observable<any> {
    return this.profileDal.getAvailableAndOutOfRoomProfiles(hospitalId, date, positions, residency, seniority);
  }

  getProfileWeekAvailability(position: string, date: string): Observable<any> {
    return this.profileDal.getProfileWeekAvailability(position, date);
  }

  getProfilesCalendars(
    profiles: Profile[] | string[],
    fromDate: Date | string,
    toDate: Date | string,
    delayTime: number = 0,
    options: any = { hrSuiteProject: false },
    scenario?: string,
    campaign?: string
  ): Observable<ProfileCalendarsResponseDto[]> {
    if (!profiles || profiles.length === 0) {
      return new Observable<ProfileCalendarsResponseDto[]>((observable: any) => {
        observable.next([]);
        observable.complete();
      });
    }
    const profilesParameter = (profiles[0] as Profile)._id
      ? (profiles as Profile[]).flatMap((profile: Profile) => (profile._id ? [profile._id] : []))
      : (profiles as string[]);
    const fromDateParameter = new Date(fromDate).toISOString();
    const toDateParameter = new Date(toDate).toISOString();

    return timer(delayTime).pipe(
      switchMap(() =>
        this.profileDal.getProfilesCalendars(
          profilesParameter,
          fromDateParameter,
          toDateParameter,
          options,
          scenario,
          campaign
        )
      )
    );
  }

  getProfilesByDayAvailability(
    position: string | undefined,
    date: string,
    descriptions: string[],
    isNight: boolean,
    page?: number,
    pageSize?: number,
    searchValue?: string,
    population?: string,
    profilesIds?: string,
    services?: string,
    residency?: string[],
    seniorities?: string,
    scenario?: string,
    campaignId?: string
  ): Observable<Profile[]> {
    return this.profileDal.getProfilesByDayAvailability(
      position,
      date,
      descriptions,
      isNight,
      page,
      pageSize,
      searchValue,
      population,
      profilesIds,
      services,
      residency,
      seniorities,
      scenario,
      campaignId
    );
  }

  getReachableProfiles(date: string, position: string): Observable<any> {
    return this.profileDal.getReachableProfiles(date, position);
  }

  getOutOfRoomNurses(date: string): Observable<Profile[]> {
    return this.profileDal.getOutOfRoomNurses(date);
  }

  getOutOfRoomIades(date: string): Observable<Profile[]> {
    return this.profileDal.getOutOfRoomIades(date);
  }

  getAllAvailableNurses(date: string): Observable<allAvailableNurses[]> {
    return this.profileDal.getAllAvailableNurses(date);
  }

  getOutOfRoomNursesFromTo(
    startDate: string,
    endDate: string,
    scenario?: string,
    campaign?: string
  ): Observable<any[]> {
    return this.profileDal.getOutOfRoomNursesFromTo(startDate, endDate, scenario, campaign);
  }

  getOutOfRoomIadesFromTo(startDate: string, endDate: string, scenario?: string, campaign?: string): Observable<any[]> {
    return this.profileDal.getOutOfRoomIadesFromTo(startDate, endDate, scenario, campaign);
  }

  getConsultationsFromTo(startDate: string, endDate: string, scenario?: string, campaign?: string): Observable<any[]> {
    return this.profileDal.getConsultationsFromTo(startDate, endDate, scenario, campaign);
  }

  getGardeFromTo(startDate: string, endDate: string, scenario?: string, campaign?: string): Observable<any[]> {
    return this.profileDal.getGardeFromTo(startDate, endDate, scenario, campaign);
  }

  getProfileById(profileId: string, delay: number = 0): Observable<Profile> {
    return timer(delay).pipe(switchMap(() => this.profileDal.getProfileById(profileId)));
  }

  getProfileHRSuiteRecap(profileId: string, startDate: string, endDate: string, hospital?: string): Observable<any> {
    return this.profileDal.getProfileHRSuiteRecap(profileId, startDate, endDate, hospital);
  }

  getProfileHRSuiteRHcodesRecap(
    profileId: string,
    startDate: string,
    endDate: string,
    hospital?: string
  ): Observable<any> {
    return this.profileDal.getProfileHRSuiteRHcodesRecap(profileId, startDate, endDate, hospital);
  }

  updateUserProfile(profile: Profile): Observable<Profile> {
    return this.profileDal.updateUserProfile(profile);
  }

  createProfileWithSkills(profileWithSkills: any): Observable<Profile> {
    return this.profileDal.createProfileWithSkills(profileWithSkills);
  }

  createDefaultSpecialtySurgeon(profile: any, specialty: string): Observable<Profile> {
    return this.profileDal.createDefaultSpecialtySurgeon(profile, specialty);
  }

  editProfileWithSkills(profile: any): Observable<Profile> {
    return this.profileDal.editProfileWithSkills(profile);
  }

  editProfile(profile: Profile): Observable<Profile> {
    return this.profileDal.editProfile(profile);
  }

  editProfileMail(profileId: string, mail: string): Observable<Profile> {
    return this.profileDal.editProfileMail(profileId, mail);
  }

  editVacationNeeds(profile: Profile, originalSpe: string): Observable<any> {
    return this.profileDal.editVacationNeeds(profile, originalSpe);
  }

  addProfile(profile: Profile): Observable<Profile> {
    return this.profileDal.addProfile(profile);
  }

  modifyProfile(profile: Profile): Observable<Profile> {
    return this.profileDal.modifyProfile(profile);
  }

  deleteProfile(idProfile: string): Observable<Profile> {
    return this.profileDal.deleteProfile(idProfile);
  }

  getNeedsCoverageBySkills(
    startDate: string,
    endDate: string,
    positions?: string,

    populationId?: string,
    scenario?: string,
    campaign?: string
  ): Observable<any> {
    return this.profileDal.getNeedsCoverageBySkills(startDate, endDate, positions, populationId, scenario, campaign);
  }

  getAverages(getAveragesInput: {
    startDate: string;
    endDate: string;
    isGarde?: boolean;
    hospitalId?: string;
    delay?: number;
    residency?: string;
    iadeProfilesIds?: string;
    positions?: string;
    scenario?: string;
    campaign?: string;
    effectifType?: string;
    requestedDocuments: {
      anesthNeeds?: boolean;
      effectif?: boolean;
      nightEffectif?: boolean;
      nightGuard?: boolean;
      nightNeeds?: boolean;
      nurseNeeds?: boolean;
      residency?: boolean;
    };
  }): Observable<any> {
    const {
      startDate,
      endDate,
      isGarde = false,
      hospitalId,
      delay = 0,
      residency = '',
      iadeProfilesIds = '',
      positions,
      scenario,
      campaign,
      effectifType,
      requestedDocuments,
    } = getAveragesInput;
    return timer(delay).pipe(
      switchMap(() =>
        this.profileDal.getAverages(
          startDate,
          endDate,
          isGarde,
          hospitalId,
          residency,
          iadeProfilesIds,
          positions,
          scenario,
          campaign,
          effectifType,
          requestedDocuments
        )
      )
    );
  }

  getAveragesForMultipleDates(input: {
    dateIntervals: Array<{ fromDate: string; toDate: string }>;
    isGarde?: boolean;
    hospitalId?: string;
    residency?: string;
    iadeProfilesIds?: string;
    positions?: string;
    scenario?: string;
    campaign?: string;
    effectifType?: string;
    requestedDocuments: {
      anesthNeeds?: boolean;
      effectif?: boolean;
      nightEffectif?: boolean;
      nightGuard?: boolean;
      nightNeeds?: boolean;
      nurseNeeds?: boolean;
      residency?: boolean;
    };
  }): Observable<any> {
    const {
      dateIntervals,
      isGarde = false,
      hospitalId,
      residency = '',
      iadeProfilesIds = '',
      positions,
      scenario,
      campaign,
      effectifType,
      requestedDocuments,
    } = input;

    return this.profileDal.getAveragesForMultipleDates(
      dateIntervals,
      isGarde,
      hospitalId,
      residency,
      iadeProfilesIds,
      positions,
      scenario,
      campaign,
      effectifType,
      requestedDocuments
    );
  }

  getNeeds(startDate: string, endDate: string): Observable<any> {
    return this.profileDal.getNeeds(startDate, endDate);
  }

  getprofileImageByscrapping(surgeonId: string, hospitalId: string) {
    return this.profileDal.getprofileImageByscrapping(surgeonId, hospitalId);
  }

  getAllProfilesIncludeDeleted(
    hospitalId?: string,
    page?: number,
    limit?: number,
    positions?: string,
    name?: string,
    sortField?: string,
    sortDirection?: '-1' | '1',
    seniorities?: string,
    specialty?: string,
    allSpecialties?: string,
    residencies?: string
  ): Observable<Profile[]> {
    return this.profileDal.getAllProfilesIncludeDeleted(
      hospitalId,
      page,
      limit,
      positions,
      name,
      sortField,
      sortDirection,
      seniorities,
      specialty,
      allSpecialties,
      residencies
    );
  }

  softRestoreProfile(idProfile: string, selectedHospitals?: string[] | undefined): Observable<Profile> {
    return this.profileDal.softRestoreProfile(idProfile, selectedHospitals);
  }

  hardRestoreProfile(idProfile: string): Observable<Profile> {
    return this.profileDal.hardRestoreProfile(idProfile);
  }

  softDeleteProfile(idProfile: string, selectedHospitals?: string[] | undefined): Observable<Profile> {
    return this.profileDal.softDeleteProfile(idProfile, selectedHospitals);
  }

  getProfilesSummary(
    position: string,
    residency: string,
    seniority: string,
    date1String: string,
    date2String: string
  ): Observable<any> {
    return this.profileDal.getProfilesSummary(position, residency, seniority, date1String, date2String);
  }

  getListWishes(profileId: string, startDate: string, endDate: string): Observable<any[]> {
    return this.profileDal.getListWishes(profileId, startDate, endDate);
  }

  getWorkingTime(profileId: string, startDate: string, endDate: string): Observable<any> {
    return this.profileDal.getWorkingTime(profileId, startDate, endDate);
  }

  getSurgeonsBySpecialty(date?: string): Observable<any> {
    return this.profileDal.getSurgeonsBySpecialty(date);
  }

  deleteDefaultSpecialtySurgeons(specialtyId: string): Observable<any> {
    return this.profileDal.deleteDefaultSpecialtySurgeons(specialtyId);
  }

  getAllSurgeons(hospital: string, date?: string): Observable<any> {
    return this.profileDal.getAllSurgeons(hospital, date);
  }

  getAllSurgeonsOnlyUser(hospital: string, date?: string): Observable<any> {
    return this.profileDal.getAllSurgeonsOnlyUser(hospital, date);
  }

  createCustomJobDescription(idProfile: string, position: string, toExcludeFromIndividual: string[]): Observable<any> {
    return this.profileDal.createCustomJobDescription(idProfile, position, toExcludeFromIndividual);
  }

  diffuseConstraint(constraintId: string, model: string, profiles: string[], position: string): Observable<any> {
    return this.profileDal.diffuseConstraint(constraintId, model, profiles, position);
  }

  addConstraint(profiles: string[], constraint: any, model: string, position: string): Observable<any> {
    return this.profileDal.addConstraint(profiles, constraint, model, position);
  }

  getProfilesWithoutTimeAlert(position: string): Observable<any> {
    return this.profileDal.getProfilesWithoutTimeAlert(position);
  }

  getAllProfilesInList(profiles: string[]): Observable<Profile[] | undefined> {
    return this.profileDal.getAllProfilesInList(profiles);
  }
}
